function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { COMPARE_FILTERS_KEY, DEFAULT_COMPARE_OPERATOR, EMPTY_COMPARE_OPERATOR, NUMERIC_RANGE_FILTER_TYPE, getCompareOperator, hasInvertedBetweenBounds, toComparableNumber } from './cabinetCompareFilters';
export const PICKLIST_FILTER_TYPE = 'picklist';

/** Keys implied by the cabinet / session — never persist into shared saved views. */
const OMIT_FROM_SAVED_VIEW = new Set(['cabinetIdentifier', 'formTypeIdentifiers', 'text', 'aiSearch', COMPARE_FILTERS_KEY]);

/**
 * Web compare UI ids ↔ shared User.content.savedViews `op` values (iOS / OS contract).
 * Empty-value uses `""`; Exactly maps to `equals`.
 */
const RUNTIME_OPERATOR_TO_STORAGE = {
  [DEFAULT_COMPARE_OPERATOR]: 'equals',
  [EMPTY_COMPARE_OPERATOR]: '',
  greaterThan: 'greaterThan',
  greaterThanOrEqual: 'greaterThanOrEqual',
  lessThan: 'lessThan',
  lessThanOrEqual: 'lessThanOrEqual',
  between: 'between'
};
const STORAGE_OPERATOR_TO_RUNTIME = {
  equals: DEFAULT_COMPARE_OPERATOR,
  '': EMPTY_COMPARE_OPERATOR,
  greaterThan: 'greaterThan',
  greaterThanOrEqual: 'greaterThanOrEqual',
  lessThan: 'lessThan',
  lessThanOrEqual: 'lessThanOrEqual',
  between: 'between'
};

/**
 * One cabinet filter selection row, matching saved-view storage (array of ids/values).
 * Cells may be `null` when the selection is cleared (“No selection”).
 *
 * @param {unknown} tuple
 * @returns {Array<string|null>}
 */
export const normalizeSelectionTuple = tuple => {
  if (!Array.isArray(tuple)) {
    if (tuple === null || tuple === '') {
      return [null];
    }
    return [String(tuple)];
  }
  return tuple.map(x => {
    if (x === null || x === '') {
      return null;
    }
    return String(x);
  });
};

/**
 * @param {unknown} values
 * @returns {Array<Array<string|null>>}
 */
const normalizePicklistValues = values => {
  if (!Array.isArray(values)) {
    return [];
  }
  return values.map(row => normalizeSelectionTuple(row));
};

/**
 * @param {string} [runtimeOperator]
 * @returns {string}
 */
const toStorageOperator = runtimeOperator => {
  if (Object.prototype.hasOwnProperty.call(RUNTIME_OPERATOR_TO_STORAGE, runtimeOperator)) {
    return RUNTIME_OPERATOR_TO_STORAGE[runtimeOperator];
  }
  return RUNTIME_OPERATOR_TO_STORAGE[DEFAULT_COMPARE_OPERATOR];
};

/**
 * @param {unknown} storageOperator
 * @returns {string}
 */
const toRuntimeOperator = storageOperator => {
  if (storageOperator === undefined || storageOperator === null) {
    return DEFAULT_COMPARE_OPERATOR;
  }
  const key = String(storageOperator);
  if (Object.prototype.hasOwnProperty.call(STORAGE_OPERATOR_TO_RUNTIME, key)) {
    return STORAGE_OPERATOR_TO_RUNTIME[key];
  }
  return DEFAULT_COMPARE_OPERATOR;
};

/**
 * Runtime compare entry (`filters.compare[index]`) → shared numericRange blob.
 *
 * Only selections that produce bounds are persisted. Readers of this shape require the
 * bounds an `op` implies — iOS `NumericRangePredicate.validateBounds` throws on `between`
 * without both ends or a comparison without `value`, and one bad entry fails the whole
 * `UserSettings` decode. A half-filled Between is stored as the single-sided operator it
 * is already behaving as at runtime.
 *
 * @param {{ operator?: string, value?: unknown, value2?: unknown }} [entry]
 * @returns {{ type: string, op: string, value?: number, min?: number, max?: number }|null}
 *   null when the selection produces no bounds and must not be persisted
 */
export const serializeNumericRangeEntry = entry => {
  const op = toStorageOperator((entry === null || entry === void 0 ? void 0 : entry.operator) || DEFAULT_COMPARE_OPERATOR);
  if (op === '') {
    return {
      type: NUMERIC_RANGE_FILTER_TYPE,
      op
    };
  }
  if (op === 'between') {
    // Inverted bounds produce no request bounds at runtime, so persisting them would
    // restore a filter that reads as a range but matches on no platform.
    if (hasInvertedBetweenBounds(entry)) {
      return null;
    }
    const min = toComparableNumber(entry === null || entry === void 0 ? void 0 : entry.value);
    const max = toComparableNumber(entry === null || entry === void 0 ? void 0 : entry.value2);
    if (min !== null && max !== null) {
      return {
        type: NUMERIC_RANGE_FILTER_TYPE,
        op,
        min,
        max
      };
    }
    if (min !== null) {
      return {
        type: NUMERIC_RANGE_FILTER_TYPE,
        op: 'greaterThanOrEqual',
        value: min
      };
    }
    if (max !== null) {
      return {
        type: NUMERIC_RANGE_FILTER_TYPE,
        op: 'lessThanOrEqual',
        value: max
      };
    }
    return null;
  }
  const value = toComparableNumber(entry === null || entry === void 0 ? void 0 : entry.value);
  if (value === null) {
    return null;
  }
  return {
    type: NUMERIC_RANGE_FILTER_TYPE,
    op,
    value
  };
};

/**
 * Shared numericRange blob → runtime compare entry.
 *
 * @param {{ op?: string, value?: unknown, min?: unknown, max?: unknown }} [stored]
 * @returns {{ operator: string, value?: unknown, value2?: unknown }}
 */
export const deserializeNumericRangeEntry = stored => {
  var _stored$value;
  const operator = toRuntimeOperator(stored === null || stored === void 0 ? void 0 : stored.op);
  if (operator === 'between') {
    var _stored$min, _stored$max;
    return {
      operator,
      value: (_stored$min = stored === null || stored === void 0 ? void 0 : stored.min) !== null && _stored$min !== void 0 ? _stored$min : '',
      value2: (_stored$max = stored === null || stored === void 0 ? void 0 : stored.max) !== null && _stored$max !== void 0 ? _stored$max : ''
    };
  }
  if (operator === EMPTY_COMPARE_OPERATOR) {
    return {
      operator
    };
  }
  return {
    operator,
    value: (_stored$value = stored === null || stored === void 0 ? void 0 : stored.value) !== null && _stored$value !== void 0 ? _stored$value : ''
  };
};

/**
 * True when a saved-view filter value is the keyed picklist / numericRange shape.
 *
 * @param {unknown} value
 * @returns {boolean}
 */
export const isKeyedSavedFilter = value => Boolean(value) && typeof value === 'object' && !Array.isArray(value) && (value.type === PICKLIST_FILTER_TYPE || value.type === NUMERIC_RANGE_FILTER_TYPE);

/**
 * Convert live `indexQuery.filters` into the shared User.content.savedViews shape.
 * Picklists become `{ type: 'picklist', values }`; numeric compare selections flatten
 * out of `filters.compare` into per-index `{ type: 'numericRange', op, … }` entries.
 *
 * @param {Record<string, unknown>} [filters]
 * @returns {Record<string, unknown>}
 */
export const serializeCabinetFiltersForSavedView = (filters = {}) => {
  const out = {};
  Object.entries(filters).forEach(([key, value]) => {
    if (OMIT_FROM_SAVED_VIEW.has(key)) {
      return;
    }
    if (Array.isArray(value)) {
      out[key] = {
        type: PICKLIST_FILTER_TYPE,
        values: normalizePicklistValues(value)
      };
      return;
    }
    if (isKeyedSavedFilter(value)) {
      out[key] = value;
      return;
    }
    // Pass through scalars (e.g. metaDeleted) that are already saved today.
    if (value !== undefined) {
      out[key] = value;
    }
  });
  const compare = filters[COMPARE_FILTERS_KEY];
  if (compare && typeof compare === 'object' && !Array.isArray(compare)) {
    Object.entries(compare).forEach(([index, entry]) => {
      if (!entry || typeof entry !== 'object') {
        return;
      }
      const serialized = serializeNumericRangeEntry(entry);
      if (serialized) {
        out[index] = serialized;
      }
    });
  }
  return out;
};

/**
 * Normalize a saved-view `filters` object into the web runtime shape:
 * picklist arrays per index, plus optional `filters.compare` for numeric ranges.
 * Accepts legacy bare arrays, legacy nested `compare`, and the keyed iOS/OS shape.
 *
 * @param {Record<string, unknown>} [filters]
 * @returns {Record<string, unknown>}
 */
export const normalizeSavedViewStyleCabinetFilters = (filters = {}) => {
  const out = {};
  const compare = {};
  Object.entries(filters).forEach(([key, value]) => {
    if (key === COMPARE_FILTERS_KEY) {
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        Object.entries(value).forEach(([index, entry]) => {
          if (entry && typeof entry === 'object' && !Array.isArray(entry)) {
            compare[index] = _objectSpread(_objectSpread({}, entry), {}, {
              operator: getCompareOperator(entry.operator).id
            });
          }
        });
      }
      return;
    }
    if (Array.isArray(value)) {
      out[key] = normalizePicklistValues(value);
      return;
    }
    if (!value || typeof value !== 'object') {
      if (value !== undefined) {
        out[key] = value;
      }
      return;
    }
    if (value.type === PICKLIST_FILTER_TYPE) {
      out[key] = normalizePicklistValues(value.values);
      return;
    }
    if (value.type === NUMERIC_RANGE_FILTER_TYPE) {
      compare[key] = deserializeNumericRangeEntry(value);
      return;
    }

    // Unknown object — leave alone so future keys are not dropped silently.
    out[key] = value;
  });
  if (Object.keys(compare).length) {
    out[COMPARE_FILTERS_KEY] = compare;
  }
  return out;
};
export default serializeCabinetFiltersForSavedView;